/**
 * Which variant (if any) can be shown in an <img> for an asset.
 *
 * Only rasterized variants qualify — the passthrough original of an EPS/PDF/MP4
 * (width 0, format "eps"/"mp4"/…) must never end up in an <img src>. But a
 * document or video that DOES carry a rendered ladder (Canto-migrated EPS/PDF
 * previews, video posters from the video-posters pipeline) should show it
 * rather than the generic placeholder.
 */
import type { ManifestAsset, ManifestVariant } from "./types.js";

const RASTER = new Set(["webp", "jpeg", "jpg", "png", "gif"]);

function rasterVariants(asset: ManifestAsset): ManifestVariant[] {
  return asset.variants.filter((v) => v.width > 0 && RASTER.has(v.format.toLowerCase()));
}

/**
 * Grid tile: the smallest rasterized variant ≥ 300px, preferring WebP (the
 * droplet disk-caches the 400 webp, so it renders instantly). Images with no
 * ladder fall back to their raster original; anything else → undefined
 * (placeholder tile).
 */
export function pickThumbnailUrl(asset: ManifestAsset): string | undefined {
  const targets = rasterVariants(asset).sort((a, b) => a.width - b.width);
  const webp = targets.find((v) => v.format === "webp" && v.width >= 300);
  if (webp) return webp.url;
  const jpeg = targets.find((v) => v.format === "jpeg" && v.width >= 300);
  if (jpeg) return jpeg.url;
  if (targets[0]) return targets[0].url;
  const orig = asset.variants[0];
  return asset.scheme === "image" && orig && RASTER.has(orig.format.toLowerCase()) ? orig.url : undefined;
}

/**
 * Modal preview: the largest WebP ≤ 1200 (the other disk-cached size), else
 * the largest rasterized variant. undefined when nothing is renderable.
 */
export function pickPreviewVariant(asset: ManifestAsset): ManifestVariant | undefined {
  const raster = rasterVariants(asset);
  return (
    raster.filter((v) => v.format === "webp" && v.width <= 1200).sort((a, b) => b.width - a.width)[0] ??
    raster.sort((a, b) => b.width - a.width)[0]
  );
}
